import { t } from '@lingui/macro';

import { ServiceType } from '@rover/constants/src/translations/service.constants';
import type { BrandCountryServices } from '@rover/shared/js/hooks/useLocalizedBrandCountryServicesByServiceType';
import { RoverBrand } from '@rover/types/src/RoverBrand';
import getRoverBrand from '@rover/utilities/getRoverBrand';

import type {
  CatalogCategoryId,
  CatalogEntry,
  CatalogTab,
  GetServiceCatalogOptions,
} from './types';
import { getServiceCatalogEntries } from '.';

/** Category tabs render in this order; the "all" rollup is appended last. */
const CATEGORY_ORDER: CatalogCategoryId[] = ['overnight', 'daytime', 'other'];

/**
 * Turn the static catalog + the services actually offered for the current
 * brand/country into the tabs the grid renders.
 *
 * Visibility:
 *  - DB-backed services (have a `serviceType`) show only when that service is
 *    offered in `servicesByType`, linked to the live landing url.
 *  - Pet Insurance and Grooming are Rover-only special tiles that aren't part of
 *    the availability data, so they show only on the Rover brand and when their
 *    feature flag is on (`hasPetInsurance` / `shouldShowGrooming`), keeping
 *    their explicit href.
 *
 * Linking: an entry's explicit `href` wins; otherwise it falls back to the live
 * landing url for its service. Entries that resolve to no link are dropped.
 *
 * Grouping: visible entries are grouped into the overnight/daytime/other tabs
 * (empty tabs omitted) followed by a synthetic "all" tab.
 *
 */
const getServiceCatalog = (
  servicesByType: BrandCountryServices | null = {},
  options: GetServiceCatalogOptions = {}
): CatalogTab[] => {
  const { hasPetInsurance = true, shouldShowGrooming = false, shouldShowTraining = true } = options;
  const offered = servicesByType ?? {};
  const isRover = getRoverBrand() === RoverBrand.Rover;

  const isVisible = (entry: CatalogEntry): boolean => {
    // Pet Insurance is the only tile with no serviceType; keyed by its stable slug.
    if (entry.serviceSlug === 'pet-insurance') return isRover && hasPetInsurance;
    if (entry.serviceType === ServiceType.GROOMING) return isRover && shouldShowGrooming;
    if (entry.serviceType === ServiceType.DOG_TRAINING) {
      return shouldShowTraining && Boolean(offered[ServiceType.DOG_TRAINING]);
    }
    return Boolean(entry.serviceType && offered[entry.serviceType]);
  };

  const resolveHref = (entry: CatalogEntry): string =>
    entry.href ?? (entry.serviceType ? offered[entry.serviceType]?.url ?? '' : '');

  const visibleEntries = getServiceCatalogEntries()
    .filter(isVisible)
    .map((entry) => ({ ...entry, href: resolveHref(entry) }))
    .filter((entry) => entry.href);

  if (visibleEntries.length === 0) return [];

  const categoryTabs = CATEGORY_ORDER.reduce<CatalogTab[]>((tabs, id) => {
    const entries = visibleEntries.filter((entry) => entry.category?.id === id);
    if (entries.length) {
      tabs.push({ id, label: entries[0].category!.label, entries });
    }
    return tabs;
  }, []);

  return [...categoryTabs, { id: 'all', label: t`All services`, entries: visibleEntries }];
};

export default getServiceCatalog;
